(function () {
  'use strict';

  const { gsap } = window;
  const { iman, BaseCarousel, layout, scrollTransitions, easePower3, normalizeValue } = window.Universe;

  /* -- AdvancedSecondaryCarousel -------------------------------------- */

  class AdvancedSecondaryCarousel {
    constructor(container) {
      this.init(container);
    }

    setup() {
      if (!this.DOM.carousel) return;

      this.carousel = new BaseCarousel({
        wrap: this.DOM.carousel,
        carouselOptions: {
          axis: 'y',
          align: 'center',
          watchDrag: false,
          containScroll: false,
        },
        onReady: () => {
          this.observeSlides();
        },
      });
    }

    observeSlides() {
      const slides = this.carousel.instance.slideNodes();
      if (!slides || slides.length === 0) return;

      this.resizeObserver = new ResizeObserver(() => {
        let maxHeight = 0;

        for (let j = 0; j < slides.length; j++) {
          const height = slides[j].getBoundingClientRect().height;
          if (height > maxHeight) maxHeight = height;
        }

        if (maxHeight === 0) return;
        if (this.cachedMaxHeight && Math.abs(this.cachedMaxHeight - maxHeight) < 1) return;

        this.cachedMaxHeight = maxHeight;
        this.DOM.carousel.style.setProperty('--slide-size', maxHeight + 'px');

        this.carousel.instance.reInit();
        this.engine = this.carousel.instance.internalEngine();
        this.engineConfigs = {
          MAX_SCROLL_DISTANCE: this.engine.scrollSnaps[this.engine.scrollSnaps.length - 1],
        };
      });

      for (let j = 0; j < slides.length; j++) {
        this.resizeObserver.observe(slides[j]);
      }
    }

    updateProgress(data) {
      if (!this.engine) return;

      const mainDistance = normalizeValue(-data.scrollProgress(), 0, 1, this.engineConfigs.MAX_SCROLL_DISTANCE);
      this.engine.translate.to(mainDistance);
    }

    resize() {}

    init(container) {
      try {
        this.DOM = {};

        this.DOM.carousel = container;

        this.setup();
      } catch (error) {
        console.log(error);
      }
    }

    destroy() {
      if (this.DOM) {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = undefined;
        }

        if (this.carousel && this.carousel.instance) {
          this.carousel.instance.destroy();
        }
        this.carousel = undefined;
        this.engine = undefined;
        this.cachedMaxHeight = undefined;
        this.DOM = undefined;
      }
    }
  }

  /* -- AdvancedMainCarousel ------------------------------------------ */

  class AdvancedMainCarousel {
    constructor(container, isMobile, onParentReady) {
      this.onParentReady = onParentReady;
      this.gsapContexts = [];
      this._cacheSlideNodes = () => {
        this.cachedSlideNodes = this.emblaApi.slideNodes();
      };
      this._setTweenScale = (event) => {
        const MAX_ROTATION = 30;
        const MAX_TRANSLATE_Y = this.isMobile ? 10 : 30;
        const MAX_SCALE_REDUCTION = 0.25;

        const scrollProgress = (event && event.scrollProgress) ? event.scrollProgress() : this.emblaApi.scrollProgress();
        const slideNodes = this.cachedSlideNodes;
        const virtualIndex = scrollProgress * (slideNodes.length - 1);

        for (let i = 0; i < slideNodes.length; i++) {
          const unitDistance = i - virtualIndex;
          const absD = Math.abs(unitDistance);
          const d = Math.min(absD, 1);

          const rotation = unitDistance * MAX_ROTATION;
          const yPercent = d * d * MAX_TRANSLATE_Y;
          const scale = 1 - d * MAX_SCALE_REDUCTION;

          slideNodes[i].style.transform = 'rotate(' + rotation + 'deg) translateY(' + yPercent + '%) scale(' + scale + ')';
        }
      };
      this._setProgress = () => {
        const autoplay = this.emblaApi.plugins() && this.emblaApi.plugins().autoplay;
        if (autoplay) autoplay.play();
        this.activeIndex = this.emblaApi.selectedScrollSnap();

        this.DOM.dots.forEach((dot) => {
          const index = Number(dot.dataset.index);

          if (index === this.activeIndex) {
            dot.classList.add('advanced-carousel__dot-container--active');
          } else {
            dot.classList.remove('advanced-carousel__dot-container--active');
          }
        });
      };
      this.onPrev = () => {
        if (this.emblaApi.canScrollPrev()) {
          this.emblaApi.scrollPrev();
        } else {
          // primo slide: torna all'ultimo (loop all'indietro)
          this.emblaApi.scrollTo(this.emblaApi.scrollSnapList().length - 1);
        }
      };
      this.onNext = () => {
        if (this.emblaApi.canScrollNext()) {
          this.emblaApi.scrollNext();
        } else {
          // ultimo slide: torna all'inizio dell'elenco
          this.emblaApi.scrollTo(0);
        }
      };
      this.init(container, isMobile);
    }

    setup(isMobile) {
      if (!this.DOM.carousel) return;

      this.carousel = new BaseCarousel({
        wrap: this.DOM.carousel,
        carouselOptions: {
          align: 'center',
          containScroll: false,
        },
        onReady: () => {
          if (this.DOM.prev) this.DOM.prev.forEach((button) => { button.addEventListener('click', this.onPrev); });
          if (this.DOM.next) this.DOM.next.forEach((button) => { button.addEventListener('click', this.onNext); });

          this.emblaApi = this.carousel.instance;
          this.initEngine();

          if (!isMobile) this.animate();

          if (this.onParentReady) this.onParentReady();
        },
      });
    }

    watchProgress() {
      if (!this.DOM.dots) return;

      this.activeIndex = 0;

      this.DOM.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.index);
          this.emblaApi.scrollTo(index);
        });
      });

      this.emblaApi.on('select', this._setProgress);
      this._setProgress();
    }

    animate() {
      this.DOM.slides.forEach((slide) => {
        const ctx = gsap.context(() => {
          const tl = gsap.timeline({
            paused: true,
            defaults: {
              duration: 0.75,
              ease: easePower3,
            },
          });

          tl.to('.js-anims-advanced-carousel-image', { scale: 0.95, yPercent: -10 }, 0);

          const handleMouseEnter = () => { tl.play(); };
          const handleMouseLeave = () => { tl.reverse(); };

          slide.addEventListener('mouseenter', handleMouseEnter);
          slide.addEventListener('mouseleave', handleMouseLeave);
          slide._eventlisteners = { handleMouseEnter, handleMouseLeave };
        }, slide);

        this.gsapContexts.push(ctx);
      });
    }

    resize(isMobile) {
      if (this.DOM) {
        this.isMobile = isMobile;

        if (isMobile) {
          this.destroyAnimation();
        } else {
          this.animate();
        }
      }
    }

    initEngine() {
      this.engine = this.emblaApi.internalEngine();
      this.isMobile = layout.window.width < 768;

      this._cacheSlideNodes();
      this._setTweenScale();

      this.emblaApi
        .on('reInit', this._cacheSlideNodes)
        .on('reInit', this._setTweenScale)
        .on('scroll', this._setTweenScale)
        .on('slideFocus', this._setTweenScale)
        .on('resize', this._cacheSlideNodes)
        .on('resize', this._setTweenScale)
        .on('pointerUp', (emblaApi) => {
          emblaApi.internalEngine().scrollBody.useFriction(0.75).useDuration(25);
        })
        .on('select', (emblaApi) => {
          emblaApi.internalEngine().scrollBody.useFriction(0.75).useDuration(25);
        });
    }

    init(container, isMobile) {
      try {
        this.DOM = {};

        this.DOM.carousel = container;
        this.DOM.prev = this.DOM.carousel.querySelectorAll('.js-advanced-carousel-prev');
        this.DOM.next = this.DOM.carousel.querySelectorAll('.js-advanced-carousel-next');
        this.DOM.dots = this.DOM.carousel.querySelectorAll('.js-advanced-carousel-dot');
        this.DOM.slides = this.DOM.carousel.querySelectorAll('.js-anims-advanced-carousel-item');

        this.setup(isMobile);
      } catch (error) {
        console.log(error);
      }
    }

    destroyAnimation() {
      if (this.gsapContexts) {
        this.gsapContexts.forEach((ctx) => { ctx.revert(); });
      }

      if (this.DOM.slides) {
        this.DOM.slides.forEach((slide) => {
          if (!slide._eventlisteners) return;

          const { handleMouseEnter, handleMouseLeave } = slide._eventlisteners;

          slide.removeEventListener('mouseenter', handleMouseEnter);
          slide.removeEventListener('mouseleave', handleMouseLeave);
          slide._eventlisteners = undefined;
        });
      }
    }

    destroyEngine() {
      this.engine = undefined;

      if (this.emblaApi) {
        this.emblaApi
          .off('reInit', this._cacheSlideNodes)
          .off('reInit', this._setTweenScale)
          .off('scroll', this._setTweenScale)
          .off('slideFocus', this._setTweenScale)
          .off('resize', this._cacheSlideNodes)
          .off('resize', this._setTweenScale);
      }
    }

    destroy() {
      if (this.DOM) {
        this.destroyEngine();
        this.destroyAnimation();
        if (this.carousel) this.carousel.destroy();

        if (this.DOM.prev) this.DOM.prev.forEach((button) => { button.removeEventListener('click', this.onPrev); });
        if (this.DOM.next) this.DOM.next.forEach((button) => { button.removeEventListener('click', this.onNext); });

        this.DOM = undefined;
      }
    }
  }

  /* -- AdvancedCarousel ----------------------------------------------- */

  class AdvancedCarousel {
    constructor(section) {
      this.init(section);
    }

    setup() {
      if (!this.DOM.mainCarousel || !this.DOM.secondaryCarousel) return;

      this.secondaryCarousel = new AdvancedSecondaryCarousel(this.DOM.secondaryCarousel);
      this.mainCarousel = new AdvancedMainCarousel(this.DOM.mainCarousel, this.isMobile, () => {
        this.mainCarousel.carousel.instance.on('scroll', (data) => { this.secondaryCarousel.updateProgress(data); });
        this.waitToShowCarousels();
      });
    }

    waitToShowCarousels() {
      if (!this.animateOnScroll) {
        this.DOM.container.classList.add('is-visible');
        this.mainCarousel.watchProgress();
        return;
      }

      this.DOM.container.dataset.scroll = 'advanced-carousel';

      if (this.animationDelay) {
        this.DOM.container.dataset.scrollDelay = this.animationDelay;
      }

      scrollTransitions.onVisible(this.DOM.container, () => {
        const transitionDuration = 1250;
        this.showTimeout = setTimeout(() => {
          this.showTimeout = undefined;
          if (this.mainCarousel) this.mainCarousel.watchProgress();
        }, transitionDuration);
      });

      scrollTransitions.observe(this.DOM.container);
    }

    resize() {
      this.isMobile = layout.window.width < 768;

      if (this.isMobile !== this.wasMobile) {
        this.wasMobile = this.isMobile;

        if (this.mainCarousel) this.mainCarousel.resize(this.isMobile);
        if (this.secondaryCarousel) this.secondaryCarousel.resize();
      }
    }

    destroy() {
      if (this.showTimeout) {
        clearTimeout(this.showTimeout);
        this.showTimeout = undefined;
      }

      if (this.DOM && this.DOM.container) {
        scrollTransitions.unobserve(this.DOM.container);
      }

      if (this.mainCarousel) {
        if (this.mainCarousel.carousel && this.mainCarousel.carousel.instance) {
          this.mainCarousel.carousel.instance.off('scroll');
        }
        this.mainCarousel.destroy();
        this.mainCarousel = undefined;
      }

      if (this.secondaryCarousel) {
        this.secondaryCarousel.destroy();
        this.secondaryCarousel = undefined;
      }
    }

    init(section) {
      if (!section) return;

      this.DOM = {};
      this.DOM.container = section;
      this.sectionId = section.closest('.shopify-section')?.id || null;

      if (this.DOM.container) {
        this.DOM.mainCarousel = this.DOM.container.querySelector('.js-advanced-main-carousel');
        this.DOM.secondaryCarousel = this.DOM.container.querySelector('.js-advanced-secondary-carousel');
        this.DOM.start = this.DOM.container.querySelector('.js-advanced-carousel-start');
        this.DOM.startButton = this.DOM.container.querySelector('.js-advanced-carousel-start-button');

        this.isMobile = layout.window.width < 768;
        this.wasMobile = this.isMobile;
        this.globalProgress = 0;
        this.animateOnScroll = this.DOM.container.dataset.animateOnScroll === 'true';
        this.animationDelay = this.DOM.container.dataset.wheelDelay;

        this.setup();
      }
    }
  }

  /* -- AdvancedCarousels ---------------------------------------------- */

  class AdvancedCarousels {
    constructor() {
      this.init();
    }

    setup() {
      for (let i = 0; i < this.DOM.sections.length; i++) {
        this.carousels.push(new AdvancedCarousel(this.DOM.sections[i]));
      }
    }

    resize() {
      if (this.DOM) {
        for (let i = 0; i < this.carousels.length; i++) {
          this.carousels[i].resize();
        }
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    reinitSection(sectionEl) {
      this.destroySection(sectionEl);

      if (!this.DOM) {
        this.DOM = {};
        this.carousels = [];
      }

      const newSections = sectionEl.querySelectorAll('.js-advanced-carousels');
      for (let i = 0; i < newSections.length; i++) {
        this.carousels.push(new AdvancedCarousel(newSections[i]));
      }

      this.DOM.sections = document.querySelectorAll('.js-advanced-carousels');
    }

    destroySection(sectionEl) {
      if (!this.carousels) return;

      const targetId = sectionEl.id || sectionEl.closest('.shopify-section')?.id;
      if (!targetId) return;

      for (let i = this.carousels.length - 1; i >= 0; i--) {
        const carousel = this.carousels[i];
        if (carousel.sectionId === targetId) {
          carousel.destroy();
          this.carousels.splice(i, 1);
        }
      }
    }

    onBlockSelect(blockEl) {
      for (let i = 0; i < this.carousels.length; i++) {
        const item = this.carousels[i];
        if (item.DOM && item.DOM.container && item.DOM.container.contains(blockEl)) {
          const slide = blockEl.closest('.js-advanced-carousel-slide');
          if (!slide) break;

          const emblaApi = item.mainCarousel && item.mainCarousel.carousel ? item.mainCarousel.carousel.instance : null;
          if (emblaApi) {
            const slides = emblaApi.slideNodes();
            const slideIndex = Array.from(slides).indexOf(slide);
            if (slideIndex !== -1) {
              const autoplay = emblaApi.plugins().autoplay;
              if (autoplay) autoplay.stop();
              emblaApi.scrollTo(slideIndex);
            }
          }
          break;
        }
      }
    }

    onBlockDeselect(blockEl) {
      for (let i = 0; i < this.carousels.length; i++) {
        const item = this.carousels[i];
        if (item.DOM && item.DOM.container && item.DOM.container.contains(blockEl)) {
          const emblaApi = item.mainCarousel && item.mainCarousel.carousel ? item.mainCarousel.carousel.instance : null;
          if (emblaApi) {
            const autoplay = emblaApi.plugins().autoplay;
            if (autoplay) autoplay.play();
          }
          break;
        }
      }
    }

    destroy() {
      if (this.DOM) {
        for (let i = 0; i < this.carousels.length; i++) {
          this.carousels[i].destroy();
        }
        this.carousels = [];
        this.DOM = undefined;
      }
    }

    init(container = document) {
      const sections = container.querySelectorAll('.js-advanced-carousels');

      if (sections.length) {
        this.DOM = {};
        this.DOM.sections = sections;

        this.carousels = [];

        this.setup();
      }
    }
  }

  /* -- Init ---------------------------------------------------------- */

  function init() {
    if (!iman.get('advancedCarousels')) {
      iman.add('advancedCarousels', new AdvancedCarousels());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Universe.AdvancedCarousels = AdvancedCarousels;
})();
